<template>
	<AppModal @hide-modal="onCancel">
		<template #header> Brief maken </template>

		<template #default>
			<div class="flex flex-col gap-4 p-6 md:flex-row">
				<div class="flex flex-col flex-1 gap-2">
					<label for="brief-tekst" class="text-sm font-medium">
						Brieftekst
					</label>
					<textarea
						id="brief-tekst"
						v-model="letterText"
						class="w-full h-64 p-3 text-sm border rounded-sm resize-none focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
						placeholder="Typ hier de tekst van de brief…"
						:disabled="isBusy"
						aria-label="Brieftekst"
					/>
					<p v-if="errorMessage" class="text-sm text-red-600" role="alert">
						{{ errorMessage }}
					</p>
				</div>

				<div class="flex flex-col flex-1 gap-2">
					<span class="text-sm font-medium">Voorbeeld</span>
					<div
						class="flex flex-col gap-4 p-4 overflow-y-auto bg-white border rounded-sm shadow-inner h-64"
						aria-label="Brief voorbeeld"
					>
						<img :src="helloLogo" alt="HelloHypotheek" class="w-24 mx-auto" />
						<span class="text-xs text-gray-500">{{ dateStamp }}</span>
						<p class="text-sm whitespace-pre-wrap break-words">
							{{ letterText || "Typ hier de tekst van de brief…" }}
						</p>
					</div>
				</div>
			</div>
		</template>

		<template #footer>
			<div class="flex justify-end gap-2">
				<AppButton variant="secondary" :disabled="isBusy" @click="onCancel">
					Annuleren
				</AppButton>
				<AppButton
					variant="primary"
					icon="picture_as_pdf"
					:loading="isBusy"
					:disabled="!canGenerate"
					@click="onGenerateAndConnect"
				>
					PDF maken en koppelen
				</AppButton>
			</div>
		</template>
	</AppModal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { mutators } from "@loanlink-nl/zero";
import AppModal from "@/components/AppModal.vue";
import AppButton from "@/components/AppButton.vue";
import helloLogo from "@/assets/hello_logo.png";
import { useZero } from "@/composables/useZero";
import { useFileUploadStore } from "@/store/useFileUploadStore";
import {
	generateLetterPdf,
	letterDateStamp,
} from "@/helpers/generateLetterPdf";

const props = withDefaults(
	defineProps<{
		requestedDocumentId: string;
		// Reactief: of het documentverzoek al een gekoppeld bestand heeft. Zo ja,
		// mag deze brief niet koppelen (dat zou een bestaande/nieuwere koppeling
		// overschrijven). De aanroeper verbergt de knop al, maar de dialog kan
		// open blijven staan terwijl een ander het document koppelt.
		alreadyLinked?: boolean;
	}>(),
	{ alreadyLinked: false },
);

const emit = defineEmits<{ close: []; cancel: [] }>();

const fileUploadStore = useFileUploadStore();

const letterText = ref("");
const isBusy = ref(false);
const errorMessage = ref<string | null>(null);

// Onthoud een reeds geüpload document zodat een mislukte koppeling opnieuw kan
// koppelen zonder een tweede upload (en dus zonder een tweede document).
const uploadedDocumentId = ref<string | null>(null);

// Wordt de tekst na een geslaagde upload nog aangepast, dan is het geüploade
// PDF-bestand verouderd. Invalideer het zodat een nieuwe poging opnieuw
// genereert en uploadt in plaats van de oude (stale) brief te koppelen.
watch(letterText, () => {
	if (uploadedDocumentId.value !== null) {
		uploadedDocumentId.value = null;
		errorMessage.value = null;
	}
});

const dateStamp = letterDateStamp(new Date());

const canGenerate = computed(
	() =>
		!isBusy.value && !props.alreadyLinked && letterText.value.trim().length > 0,
);

function onCancel() {
	if (isBusy.value) {
		return;
	}
	emit("cancel");
	emit("close");
}

async function connect(documentId: string): Promise<void> {
	const z = useZero();
	await z.value.mutate(
		mutators.hdnRequestedDocument.connect({
			actionId: crypto.randomUUID(),
			documentId,
			id: props.requestedDocumentId,
		}),
	).server;
}

async function onGenerateAndConnect() {
	// Er is intussen al een bestand gekoppeld: niet overschrijven. Melden en
	// stoppen; de gebruiker kan de dialog sluiten.
	if (props.alreadyLinked) {
		errorMessage.value =
			"Er is inmiddels al een bestand gekoppeld aan dit documentverzoek. Sluit dit venster om het te bekijken.";
		return;
	}

	// Dubbelklik-bescherming: één klik tegelijk verwerken.
	if (isBusy.value || !canGenerate.value) {
		return;
	}

	isBusy.value = true;
	errorMessage.value = null;

	try {
		// Retry na koppelfout: hergebruik het bestaande document-id, geen
		// tweede render en geen tweede upload.
		if (uploadedDocumentId.value === null) {
			const file = generateLetterPdf(letterText.value);
			const documentId = await fileUploadStore.uploadFile({
				file,
				newName: file.name.replace(/\.pdf$/i, ""),
			});
			uploadedDocumentId.value = documentId;
		}

		// De upload kan even duren; in die tijd kan een ander een bestand hebben
		// gekoppeld. Opnieuw controleren vlak vóór connect voorkomt dat we die
		// nieuwere koppeling overschrijven. Het document is al geüpload en blijft
		// bewaard; de gebruiker kan het venster sluiten.
		if (props.alreadyLinked) {
			errorMessage.value =
				"Er is inmiddels al een bestand gekoppeld aan dit documentverzoek. Sluit dit venster om het te bekijken.";
			return;
		}

		await connect(uploadedDocumentId.value);

		emit("close");
	} catch (error) {
		if (uploadedDocumentId.value === null) {
			// Render- of uploadfout: tekst blijft staan, er is niet gekoppeld.
			errorMessage.value =
				"De brief kon niet worden gemaakt. De tekst is bewaard; probeer het opnieuw.";
		} else {
			// Upload gelukt, koppelen mislukt: melden en opnieuw laten koppelen.
			errorMessage.value =
				"De brief is geüpload maar kon niet worden gekoppeld. Probeer het koppelen opnieuw.";
		}
		void error;
	} finally {
		isBusy.value = false;
	}
}
</script>
